// WorkspaceInfoView — long-form "Competition info" body for the student
// program workspace. Two-column layout (1.55fr / 1fr on wide screens):
//
//   LEFT  — About this competition (multi-paragraph brief), Format key/value
//           table, Eligibility paragraph + bullet pills.
//   RIGHT — At a glance key/value summary, Deliverables timeline list.
//
// Anything the schema doesn't yet model (prizes, resources, liaison
// contact) is intentionally omitted — those sections will appear once the
// matching backend fields exist.

import { Platform, Text, View } from "react-native";
import { COLORS } from "../../../lib/brand";

import type {
  CompetitionStatus,
  CompetitionType,
  Deliverable,
} from "../../../types/database";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const SAND_LINE = COLORS.SAND_LINE.hex;
const TEXT = "#1a1a18";
const TEXT_MUTED = "rgba(28,43,26,0.62)";
const TEXT_LIGHT = "rgba(28,43,26,0.42)";

/** Fields the info view actually reads — kept structural so both
 *  `Competition` and `CompetitionDetail` callers satisfy it. */
export type WorkspaceInfoCompetition = {
  brief: string | null;
  deliverable_description: string | null;
  status: CompetitionStatus;
  opens_at: string | null;
  closes_at: string | null;
  team_size_min: number;
  max_team_size: number;
  competition_type: CompetitionType;
  eligibility_schools: string[] | null;
  eligibility_majors: string[] | null;
  eligibility_grad_year_min: number | null;
  eligibility_grad_year_max: number | null;
  company_name?: string | null;
};

export type WorkspaceInfoViewProps = {
  competition: WorkspaceInfoCompetition;
  deliverables: Deliverable[];
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "TBD";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "TBD";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fmtDateShort(iso: string | null | undefined): string {
  if (!iso) return "TBD";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "TBD";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function competitionTypeLabel(type: CompetitionType): string {
  if (type === "invite_only") return "Invite-only case competition";
  if (type === "uncapped") return "Open enrollment case competition";
  return "Case competition";
}

function statusLabel(status: CompetitionStatus): string {
  if (status === "open") return "Registration open";
  if (status === "judging") return "In judging";
  if (status === "closed") return "Closed";
  return "Draft";
}

function teamSizePhrase(min: number, max: number): string {
  if (min === max) return `Teams of ${max}`;
  return `Teams of ${min}–${max}`;
}

export function WorkspaceInfoView({
  competition,
  deliverables,
}: WorkspaceInfoViewProps) {
  const briefParagraphs = (competition.brief ?? "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  const totalPoints = deliverables.reduce(
    (acc, d) => acc + (d.is_scored && typeof d.point_value === "number" ? d.point_value : 0),
    0
  );
  const sortedDeliverables = [...deliverables].sort(
    (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
  );

  const formatRows: { k: string; v: string }[] = [
    { k: "Roster", v: teamSizePhrase(competition.team_size_min, competition.max_team_size) },
    { k: "Format", v: competitionTypeLabel(competition.competition_type) },
    {
      k: "Deliverables",
      v:
        deliverables.length === 0
          ? "Pending"
          : deliverables.length === 1
          ? "1 milestone"
          : `${deliverables.length} milestones`,
    },
    {
      k: "Scoring",
      v:
        totalPoints > 0
          ? `${totalPoints} pts across ${deliverables.length} deliverables`
          : "Captain review pass / fail per deliverable",
    },
    { k: "Status", v: statusLabel(competition.status) },
  ];

  // "At a glance" right-rail card.
  const glanceRows: { k: string; v: string }[] = [
    { k: "Company", v: competition.company_name?.trim() || "—" },
    { k: "Type", v: competitionTypeLabel(competition.competition_type) },
    { k: "Opens", v: fmtDate(competition.opens_at) },
    { k: "Closes", v: fmtDate(competition.closes_at) },
    {
      k: "Team size",
      v: teamSizePhrase(competition.team_size_min, competition.max_team_size),
    },
    { k: "Status", v: statusLabel(competition.status) },
  ];

  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 28,
        alignItems: "flex-start",
      }}
    >
      {/* LEFT — long-form */}
      <View style={{ minWidth: 320, flexBasis: 0, flexGrow: 1.55, gap: 28 }}>
        <Section title="About this competition">
          {briefParagraphs.length === 0 ? (
            <Text
              style={{
                fontFamily: "DMSans_400Regular",
                fontSize: 14,
                lineHeight: 24,
                color: TEXT_MUTED,
              }}
            >
              The competition brief hasn’t been published yet. Check back soon —
              the host typically drops it ahead of the program’s open date.
            </Text>
          ) : (
            <View style={{ gap: 14 }}>
              {briefParagraphs.map((p, i) => (
                <Text
                  key={i}
                  style={{
                    fontFamily: "DMSans_400Regular",
                    fontSize: 14.5,
                    lineHeight: 25,
                    color: TEXT,
                  }}
                >
                  {p}
                </Text>
              ))}
              {competition.deliverable_description ? (
                <Text
                  style={{
                    fontFamily: "DMSans_400Regular",
                    fontSize: 14.5,
                    lineHeight: 25,
                    color: TEXT,
                  }}
                >
                  {competition.deliverable_description}
                </Text>
              ) : null}
            </View>
          )}
        </Section>

        <Section title="Format & rules">
          <Card>
            {formatRows.map((r, i) => (
              <KvRow
                key={r.k}
                label={r.k}
                value={r.v}
                isLast={i === formatRows.length - 1}
              />
            ))}
          </Card>
        </Section>

        <Section title="Eligibility">
          <EligibilityBlock competition={competition} />
        </Section>
      </View>

      {/* RIGHT — at-a-glance + deliverables timeline */}
      <View style={{ minWidth: 280, flexBasis: 0, flexGrow: 1, gap: 18 }}>
        <Card>
          <Overline>At a glance</Overline>
          <View style={{ marginTop: 10 }}>
            {glanceRows.map((r, i) => (
              <GlanceRow
                key={r.k}
                label={r.k}
                value={r.v}
                isLast={i === glanceRows.length - 1}
              />
            ))}
          </View>
        </Card>

        {sortedDeliverables.length > 0 ? (
          <Card>
            <Overline>Deliverables</Overline>
            <View style={{ marginTop: 10 }}>
              {sortedDeliverables.map((d, i) => (
                <DeliverableRow
                  key={d.id}
                  index={d.sequence}
                  name={d.name}
                  dueAt={d.due_at}
                  isLast={i === sortedDeliverables.length - 1}
                />
              ))}
            </View>
          </Card>
        ) : null}
      </View>
    </View>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <View>
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 10.5,
          letterSpacing: 2.1,
          textTransform: "uppercase",
          color: TEXT_MUTED,
          marginBottom: 14,
        }}
      >
        {title}
      </Text>
      {children}
    </View>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <View
      style={[
        {
          backgroundColor: COLORS.WHITE.hex,
          borderRadius: 8,
          borderWidth: 1,
          borderColor: "rgba(28,43,26,0.10)",
          paddingVertical: 16,
          paddingHorizontal: 22,
        },
        Platform.OS === "web"
          ? ({
              boxShadow:
                "0 1px 2px rgba(28,43,26,0.04), 0 4px 14px rgba(28,43,26,0.05)",
            } as object)
          : null,
      ]}
    >
      {children}
    </View>
  );
}

function Overline({ children }: { children: React.ReactNode }) {
  return (
    <Text
      style={{
        fontFamily: "DMSans_700Bold",
        fontSize: 9.5,
        letterSpacing: 2.1,
        textTransform: "uppercase",
        color: AMBER,
      }}
    >
      {children}
    </Text>
  );
}

function KvRow({
  label,
  value,
  isLast,
}: {
  label: string;
  value: string;
  isLast: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        gap: 16,
        paddingVertical: 10,
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: SAND_LINE,
      }}
    >
      <Text
        style={{
          width: 140,
          fontFamily: "DMSans_700Bold",
          fontSize: 10,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: TEXT_LIGHT,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          flex: 1,
          fontFamily: "DMSans_400Regular",
          fontSize: 13.5,
          lineHeight: 20,
          color: TEXT,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

function GlanceRow({
  label,
  value,
  isLast,
}: {
  label: string;
  value: string;
  isLast: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        paddingVertical: 10,
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: SAND_LINE,
      }}
    >
      <Text
        style={{
          fontFamily: "DMSans_500Medium",
          fontSize: 12.5,
          color: TEXT_LIGHT,
        }}
      >
        {label}
      </Text>
      <Text
        numberOfLines={1}
        style={{
          flexShrink: 1,
          textAlign: "right",
          fontFamily: "DMSans_500Medium",
          fontSize: 13,
          color: TEXT,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

function DeliverableRow({
  index,
  name,
  dueAt,
  isLast,
}: {
  index: number;
  name: string;
  dueAt: string | null;
  isLast: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "baseline",
        gap: 12,
        paddingVertical: 10,
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: SAND_LINE,
      }}
    >
      <Text
        style={{
          width: 22,
          fontFamily: "CormorantGaramond_500Medium",
          fontSize: 16,
          color: AMBER,
          fontVariant: ["tabular-nums"],
        }}
      >
        {index}
      </Text>
      <Text
        numberOfLines={2}
        style={{
          flex: 1,
          fontFamily: "CormorantGaramond_500Medium",
          fontSize: 14,
          lineHeight: 18,
          color: TEXT,
        }}
      >
        {name}
      </Text>
      <Text
        style={{
          fontFamily: "DMSans_500Medium",
          fontSize: 11.5,
          color: TEXT_MUTED,
          fontVariant: ["tabular-nums"],
        }}
      >
        {fmtDateShort(dueAt)}
      </Text>
    </View>
  );
}

function EligibilityBlock({
  competition,
}: {
  competition: WorkspaceInfoCompetition;
}) {
  const schools = competition.eligibility_schools ?? [];
  const majors = competition.eligibility_majors ?? [];
  const minY = competition.eligibility_grad_year_min;
  const maxY = competition.eligibility_grad_year_max;

  const gradLabel = (() => {
    if (minY != null && maxY != null) return `Class of ${minY}–${maxY}`;
    if (minY != null) return `Class of ${minY} and later`;
    if (maxY != null) return `Class of ${maxY} and earlier`;
    return "All graduation years";
  })();

  const groups: { label: string; items: string[]; allLabel: string }[] = [
    { label: "Schools", items: schools, allLabel: "Open to all schools" },
    { label: "Majors", items: majors, allLabel: "All majors" },
  ];

  return (
    <View style={{ gap: 14 }}>
      {groups.map((g) => (
        <View key={g.label} style={{ gap: 6 }}>
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: 10,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: TEXT_LIGHT,
            }}
          >
            {g.label}
          </Text>
          {g.items.length === 0 ? (
            <Text
              style={{
                fontFamily: "DMSans_400Regular",
                fontSize: 13.5,
                color: TEXT,
              }}
            >
              {g.allLabel}
            </Text>
          ) : (
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
              {g.items.map((item) => (
                <View
                  key={item}
                  style={{
                    paddingHorizontal: 10,
                    paddingVertical: 4,
                    borderRadius: 999,
                    backgroundColor: "rgba(28,43,26,0.05)",
                    borderWidth: 1,
                    borderColor: "rgba(28,43,26,0.10)",
                  }}
                >
                  <Text
                    style={{
                      fontFamily: "DMSans_500Medium",
                      fontSize: 11.5,
                      color: TEXT,
                    }}
                  >
                    {item}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>
      ))}
      <View style={{ gap: 6 }}>
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 10,
            letterSpacing: 1.6,
            textTransform: "uppercase",
            color: TEXT_LIGHT,
          }}
        >
          Graduation year
        </Text>
        <Text
          style={{
            fontFamily: "DMSans_400Regular",
            fontSize: 13.5,
            color: TEXT,
          }}
        >
          {gradLabel}
        </Text>
      </View>
    </View>
  );
}

// Reference unused token to keep editors from stripping it.
void FOREST;
